import { flatten, safeParseInt } from 'shared/util'
import { Box, Divider, LinearProgress, List, ListItem, ListItemButton, Stack, StackProps, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import { SisetuResult } from 'interface/sisetu/types'
import { CompareModeType } from "features/dpcResult/types"

import { Loading } from 'components/controls/loading'
import { ErrorBlock } from 'components/controls/errorInfo'

import { useSisetuDpc } from './container'
import { YearSelect } from './components/yearSelect'
import { MdcSelect } from './components/mdcSelect'
import { DpcNendoGraph } from './components/nendoGraph'
import { DpcTable } from './components/table'
import { DpcCompareGraph } from './components/compareGraph'
import { useSisetuPreview } from '../../container'

type SisetuPreviewDpcPageProps = {
  sisetu: SisetuResult
  container: ReturnType<typeof useSisetuPreview>
} & StackProps
export const SisetuPreviewDpcPage = ({
  sisetu,
  //container,
  ...others
}: SisetuPreviewDpcPageProps) => {
  const {
		loading, error,
		selectedYear, setSelectedYear, yearList, currentYear,
		selectedMdcCode, setSelectedMdcCode, mdcList,
		selectedDpcCode, setSelectedDpcCode, dpcList, currentDisease,
    compareMode, setCompareMode, compareResult, nendoResult
  } = useSisetuDpc({ sisetu })

  return (
    <Stack
      {...others}
      direction="column"
      position="relative"
      width='100%'
      height='100%'
      boxSizing='border-box'
    >
      {loading && <Loading />}

      <Stack
        direction="row"
        width='100%'
        height='100%'
        overflow="hidden"
      >
        <Stack
          flexShrink={0}
          direction="column"
          boxSizing='border-box'
          width="30rem"
        >
          <Stack flexShrink={0} direction="row" width='100%' padding={1} spacing={1} boxSizing="border-box">
            <Box flexShrink={0}>
              <YearSelect
                name="year-choice"
                list={yearList.map(y => `${y.year}`)}
                value={selectedYear ? `${selectedYear}` : ''}
                onChange={(event) => setSelectedYear(safeParseInt(event.target.value) ?? null)}
                variant="outlined"
                size="small"
                fullWidth
              />            
            </Box>
            <Box flexGrow={1} overflow="hidden">
              <MdcSelect
                name="mdc-choice"
                list={mdcList}
                value={selectedMdcCode ?? ''}
                maxCases={currentYear?.maxCasesMdc ?? 0}
                onChange={(event) => setSelectedMdcCode(event.target.value)}
                variant="outlined"
                size="small"
                fullWidth
              />            
            </Box>
          </Stack>

          <Divider/>

          <Box
            flexGrow={1}
            overflow='auto'
          >
            <List disablePadding>
              {dpcList.map(dpc => (
                <ListItem key={`dpc-${dpc.code}`} disablePadding>
                  <ListItemButton
                    selected={dpc.code === selectedDpcCode}
                    onClick={() => setSelectedDpcCode(dpc.code)}
                  >
                    <Stack
                      direction="row"
                      width="100%"
                      boxSizing="border-box"
                      alignItems="center"
                      spacing={1}
                    >
                      <Typography flexGrow={1} variant="body1" component="div">{dpc.name}</Typography>
                      <LinearProgress
                        variant="determinate"
                        sx={{ flexShrink: 0, height: "1rem", width: "3rem", borderRadius: 1 }}
                        value={currentYear?.maxCasesDpc ? ((dpc.totalCases * 100) / currentYear.maxCasesDpc) : 0}
                      />
                    </Stack>
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Box>
        </Stack>

        <Divider orientation="vertical"/>

        <Stack direction="column" flexGrow={1} overflow="hidden">
          <Stack direction="row" padding={1} spacing={1} width="100%" boxSizing="border-box">
            <ToggleButtonGroup
              color="primary"
              value={compareMode}
              exclusive
              onChange={(event, value: string) => {
                if (value && value.length) {
                  setCompareMode(value as CompareModeType)
                }
              }}
              aria-label="compare-mode"
              size="small"
            >
              <ToggleButton value="nendo" sx={{ whiteSpace: 'nowrap' }}>
                年度推移
              </ToggleButton>
              <ToggleButton value="iryoken" sx={{ whiteSpace: 'nowrap' }}>
                医療圏内分布
              </ToggleButton>
              <ToggleButton value="todofuken" sx={{ whiteSpace: 'nowrap' }}>
                都道府県内分布
              </ToggleButton>
              <ToggleButton value="all" sx={{ whiteSpace: 'nowrap' }}>
                全国分布
              </ToggleButton>
            </ToggleButtonGroup>
          </Stack>

          <ErrorBlock error={flatten(error)} padding={1} />

          {compareMode === 'nendo' ? (
            <>
              <DpcNendoGraph
                list={nendoResult}
                style={{ flexGrow: 1 }}
                width="auto"
              />
              <Divider/>
              <DpcTable
                list={nendoResult}
                sx={{ maxHeight: '50%' }}
              />
            </>
          ) : currentDisease ? (
            <DpcCompareGraph
              disease={currentDisease}
              sisetu={sisetu}
              results={compareResult}
              style={{ flexGrow: 1 }}
            />
          ) : <></>}
        </Stack>
      </Stack>
    </Stack>
  )
}
